/**
 * Canonical set-name normalization (decided 2026-07-10).
 *
 * Canonical form: PLAIN names, no TCGCSV era prefix ("Evolving Skies", not
 * "SWSH07: Evolving Skies"); promo sets use the official API "Black Star"
 * names ("SWSH Black Star Promos"). A one-time SQL migration normalized all
 * existing rows; this function keeps every future write consistent. It is
 * idempotent — safe to run on every save.
 *
 * Call this at EVERY point that writes a Set: saveCard / saveSealed (safety
 * net for manual typing), the purchase / trade / adjustment item pickers and
 * lot creation, the sealed catalog picker, the catalog card grid, bulk scan,
 * and rip pulls.
 */
const ERA = "SV|SWSH|SM|XY|BW|ME|DP|HGSS|POP|EX";

const ALIASES: Record<string, string> = {
  // Promo unification — official API names win so Lookups never re-diverge.
  "sword & shield promo":               "SWSH Black Star Promos",
  "sword & shield promos":              "SWSH Black Star Promos",
  "sword & shield black star promos":   "SWSH Black Star Promos",
  "sun & moon promo":                   "SM Black Star Promos",
  "sun & moon promos":                  "SM Black Star Promos",
  "sun & moon black star promos":       "SM Black Star Promos",
  "scarlet & violet promo":             "SV Black Star Promos",
  "scarlet & violet promos":            "SV Black Star Promos",
  "scarlet & violet black star promos": "SV Black Star Promos",
  "mega evolution promos":              "ME Black Star Promos",
  // Punctuation / casing one-offs
  "crown zenith galarian gallery":      "Crown Zenith: Galarian Gallery",
  "pokemon go":                         "Pokemon GO",
  "pok\u00e9mon go":                      "Pokemon GO",
};

export function normalizeSetName(raw: string | null | undefined): string {
  let s = String(raw ?? "").replace(/\s+/g, " ").trim();
  if (!s) return "";
  // Era-prefixed forms: "SV08: Name", "ME: Name", "XY - Name". When the
  // remainder is just "Black Star Promos", KEEP the era code in the name
  // (that's the canonical promo-set name).
  const m = s.match(new RegExp(`^(${ERA})[0-9.]{0,4}\\s*(?::|\\s-\\s)\\s*(.+)$`, "i"));
  if (m) {
    const rest = m[2].trim();
    s = /^black star promos$/i.test(rest)
      ? `${m[1].toUpperCase()} Black Star Promos`
      : rest;
  } else {
    // Generic uppercase catalog codes outside the era list (e.g. "OP05: ...").
    s = s.replace(/^[A-Z0-9]{2,10}:\s*/, "").trim();
  }
  const alias = ALIASES[s.toLowerCase()];
  return alias || s;
}
